import { useState } from "react";
import { ArrowRight, ArrowDown, Check, MapPin, IdCard, LayoutGrid, HeartHandshake, Users, Wrench, Loader2, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { useDocumentMeta } from "@/lib/seo";
import { api, formatApiErrorDetail } from "@/lib/api";

const PETROLE = "#284E55";

const ADVANTAGES = [
  { icon: MapPin, title: "Un secteur pour développer votre activité", text: "Développez votre présence locale sur le secteur défini avec CozyHome." },
  { icon: IdCard, title: "Une identité professionnelle", text: "Profitez de l'image CozyHome et de supports de communication homogènes." },
  { icon: LayoutGrid, title: "Des outils pour piloter votre activité", text: "Accédez à votre espace Partenaire pour suivre prospects, clients, projets et activité." },
  { icon: HeartHandshake, title: "Un accompagnement commercial", text: "Bénéficiez des méthodes et outils CozyHome pour structurer et développer votre activité." },
  { icon: Users, title: "Un réseau d'entreprises", text: "Accédez aux entreprises et artisans référencés dans le réseau CozyHome." },
  { icon: Wrench, title: "Un appui technique", text: "Confiez vos plans, dessins et dossiers techniques à CozyHome Central : pas besoin d'avoir un dessinateur en interne." },
];

const STEPS = [
  ["Candidature", "Vous nous présentez votre profil et le secteur sur lequel vous souhaitez développer votre activité."],
  ["Dossier professionnel", "Les justificatifs nécessaires sont demandés uniquement après une première étude de votre profil."],
  ["Validation CozyHome", "Nos équipes examinent votre dossier et vérifient à nouveau la disponibilité du secteur."],
  ["Souscription", "Vous choisissez votre secteur et votre abonnement CozyHome."],
  ["Espace Partenaire", "Votre espace professionnel s'ouvre — le secteur reste en attente d'activation."],
  ["Activation du territoire", "CozyHome active votre secteur dès que le dossier est entièrement conforme."],
  ["Accès complet", "Toutes les fonctions de l'Espace Partenaire sont ouvertes, les projets commencent à arriver."],
];

const SITUATIONS = [
  { value: "installe", label: "Je suis déjà installé / mon entreprise existe" },
  { value: "en_creation", label: "Mon entreprise est en cours de création" },
  { value: "a_creer", label: "Je souhaite créer mon activité pour rejoindre CozyHome" },
];

const EXPERIENCES = [
  { value: "aucune", label: "Aucune" },
  { value: "moins_2_ans", label: "Moins de 2 ans" },
  { value: "2_5_ans", label: "2 à 5 ans" },
  { value: "5_10_ans", label: "5 à 10 ans" },
  { value: "plus_10_ans", label: "Plus de 10 ans" },
];

const EMPTY_FORM = {
  firstname: "", lastname: "", phone: "", email: "",
  situation: "", company_name: "", siret: "",
  city: "", postal_code: "", department: "", sector: "",
  already_moe: "", experience: "",
  message: "", consent: false, intended_territory_id: null,
};

const inputClass =
  "w-full rounded-2xl border-2 border-[#EDEAE3] bg-white px-5 py-3.5 text-base font-semibold text-navy placeholder:font-normal placeholder:text-stone-400 focus:border-[#284E55] focus:outline-none";

const Pill = ({ selected, onClick, label, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    aria-pressed={selected}
    className={`rounded-full border-2 px-5 py-3 text-sm font-bold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#284E55] ${
      selected ? "border-[#284E55] bg-[#284E55] text-white" : "border-[#EDEAE3] bg-white text-navy hover:border-[#284E55]/50"
    }`}
  >
    {label}
  </button>
);

const TerritoryCheck = ({ postalCode, city, chosenId, onChoose }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const check = async () => {
    if (!/^\d{5}$/.test(postalCode.trim())) {
      toast.error("Renseignez un code postal à 5 chiffres pour vérifier votre secteur.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.get("/territories/check", { params: { postal_code: postalCode.trim(), city: city.trim() || undefined } });
      setResult(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="territory-check-widget" className="mt-4 rounded-2xl border-2 border-dashed border-[#EDEAE3] bg-cream/50 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs leading-relaxed text-stone-500">
          Vérifiez si votre secteur est disponible avant de candidater — ce choix n'est pas une attribution définitive.
        </p>
        <button
          type="button"
          data-testid="territory-check-submit"
          onClick={check}
          disabled={loading}
          className="flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold text-white transition-opacity duration-300 hover:opacity-90 disabled:opacity-60"
          style={{ backgroundColor: PETROLE }}
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          Vérifier mon secteur
        </button>
      </div>

      {result && (
        <div data-testid="territory-check-result" className="mt-4 space-y-2">
          {!result.found && (
            <p className="rounded-xl bg-white px-4 py-3 text-sm text-stone-500">
              Secteur non identifié pour le moment — un conseiller CozyHome reviendra vers vous.
            </p>
          )}
          {result.found && result.territory.status === "disponible" && (
            <label
              data-testid="territory-check-primary-available"
              className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 px-4 py-3.5 transition-colors duration-200 ${
                chosenId === result.territory.id ? "border-[#284E55] bg-[#284E55]/5" : "border-[#EDEAE3] bg-white"
              }`}
            >
              <input type="radio" className="mt-1 h-4 w-4 accent-[#284E55]" checked={chosenId === result.territory.id} onChange={() => onChoose(result.territory)} />
              <span>
                <span className="flex items-center gap-1.5 text-sm font-bold text-navy">
                  <Check className="h-3.5 w-3.5" style={{ color: PETROLE }} /> Secteur disponible : {result.territory.name}
                </span>
                <span className="mt-0.5 block text-xs text-stone-500">{result.territory.department}</span>
              </span>
            </label>
          )}
          {result.found && result.territory.status === "occupe" && (
            <p data-testid="territory-check-primary-occupied" className="rounded-xl bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-700">
              Ce secteur est déjà occupé par un partenaire CozyHome.
              {result.neighbors_available.length > 0
                ? " Vous pouvez choisir un secteur limitrophe disponible ci-dessous."
                : " Aucun secteur limitrophe n'est disponible pour le moment — votre candidature reste possible, CozyHome vous recontactera."}
            </p>
          )}
          {result.neighbors_available.length > 0 && (
            <div>
              <p className="pt-1 text-xs font-bold uppercase tracking-wider text-stone-400">Secteurs limitrophes disponibles</p>
              <div className="mt-2 space-y-2">
                {result.neighbors_available.map((t) => (
                  <label
                    key={t.id}
                    data-testid={`territory-check-neighbor-${t.code}`}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border-2 px-4 py-3.5 transition-colors duration-200 ${
                      chosenId === t.id ? "border-[#284E55] bg-[#284E55]/5" : "border-[#EDEAE3] bg-white"
                    }`}
                  >
                    <input type="radio" className="mt-1 h-4 w-4 accent-[#284E55]" checked={chosenId === t.id} onChange={() => onChoose(t)} />
                    <span>
                      <span className="text-sm font-bold text-navy">{t.name}</span>
                      <span className="mt-0.5 block text-xs text-stone-500">{t.department}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function PartnerApplicationPage() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useDocumentMeta(
    "Devenir Partenaire CozyHome — Candidater pour mon secteur | CozyHome",
    "Rejoignez un réseau de maîtres d'œuvre indépendants et développez votre activité avec l'appui de la centrale CozyHome. Candidature simple, sans document ni paiement."
  );

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const scrollToForm = () => {
    document.getElementById("candidature")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstname.trim() || !form.lastname.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Merci de renseigner vos coordonnées.");
      return;
    }
    if (!form.situation) {
      toast.error("Merci d'indiquer votre situation professionnelle.");
      return;
    }
    if (!form.city.trim() || !form.postal_code.trim() || !form.department.trim() || !form.sector.trim()) {
      toast.error("Merci de renseigner votre localisation et votre secteur souhaité.");
      return;
    }
    if (!/^\d{5}$/.test(form.postal_code.trim())) {
      toast.error("Le code postal doit contenir 5 chiffres.");
      return;
    }
    if (form.already_moe === "") {
      toast.error("Merci d'indiquer si vous êtes déjà maître d'œuvre.");
      return;
    }
    if (!form.experience) {
      toast.error("Merci d'indiquer votre expérience dans le bâtiment / la rénovation.");
      return;
    }
    if (!form.consent) {
      toast.error("Merci d'accepter le traitement de votre candidature.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/partner-applications", {
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        situation: form.situation,
        company_name: form.company_name.trim(),
        siret: form.siret.trim(),
        city: form.city.trim(),
        postal_code: form.postal_code.trim(),
        department: form.department.trim(),
        sector: form.sector.trim(),
        already_moe: form.already_moe === "oui",
        experience: form.experience,
        message: form.message.trim(),
        consent: form.consent,
        intended_territory_id: form.intended_territory_id || null,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      {/* ---------------- Hero ---------------- */}
      <section data-testid="page-partenaire-candidature-hero" className="bg-ivory pb-16 pt-32 sm:pb-20 sm:pt-36">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] sm:text-sm" style={{ color: PETROLE }}>
            Devenir Partenaire CozyHome
          </p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-[1.08] tracking-tight text-navy sm:text-6xl">
            Développez votre activité. <span className="italic" style={{ color: PETROLE }}>CozyHome vous accompagne.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-stone-500 sm:text-base">
            Rejoignez un réseau de maîtres d'œuvre indépendants et bénéficiez d'une identité forte, d'outils
            professionnels et de l'appui de la centrale CozyHome pour développer votre activité.
          </p>
          <button
            data-testid="partenaire-candidature-hero-cta"
            onClick={scrollToForm}
            className="group mt-8 inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-bold text-white transition-opacity duration-300 hover:opacity-90"
            style={{ backgroundColor: PETROLE }}
          >
            Candidater pour mon secteur
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
        </div>
      </section>

      <section className="bg-ivory pb-24 sm:pb-32">
        <div className="mx-auto max-w-7xl space-y-10 px-5 sm:px-8">
          {/* ---------------- Présentation ---------------- */}
          <Reveal>
            <article data-testid="partenaire-candidature-presentation" className="rounded-3xl bg-white p-7 sm:p-12">
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: PETROLE }}>Le principe</p>
              <h2 className="mt-4 max-w-3xl font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">
                Restez indépendant, profitez de la force d'un réseau
              </h2>
              <div className="mt-5 max-w-3xl space-y-4 text-sm leading-relaxed text-stone-600 sm:text-base">
                <p>
                  En rejoignant CozyHome, vous restez un professionnel indépendant et développez votre propre
                  activité sur votre secteur. Vous accompagnez vos clients dans leurs projets et facturez
                  directement vos missions.
                </p>
                <p>
                  CozyHome vous apporte une identité, des outils, des services et un réseau pour vous permettre de
                  vous concentrer sur l'essentiel : développer votre activité et accompagner vos clients.
                </p>
              </div>
            </article>
          </Reveal>

          {/* ---------------- Avantages ---------------- */}
          <Reveal>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: PETROLE }}>Ce que vous apporte le réseau</p>
              <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">Six avantages concrets</h2>
              <div data-testid="partenaire-candidature-avantages" className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {ADVANTAGES.map((a, i) => (
                  <div key={a.title} data-testid={`partenaire-avantage-${i}`} className="rounded-3xl bg-white p-7">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: `${PETROLE}14`, color: PETROLE }}>
                      <a.icon className="h-4.5 w-4.5" />
                    </span>
                    <p className="mt-4 text-sm font-bold text-navy">{i + 1} — {a.title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-stone-500">{a.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* ---------------- Tarif ---------------- */}
          <Reveal>
            <article data-testid="partenaire-candidature-tarif" className="overflow-hidden rounded-3xl text-white" style={{ backgroundColor: PETROLE }}>
              <div className="p-7 sm:p-12">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/70">Rejoindre le réseau CozyHome</p>
                <p data-testid="partenaire-candidature-prix" className="mt-4 font-display text-4xl font-medium tracking-tight sm:text-5xl">
                  249 € <span className="text-xl text-white/70 sm:text-2xl">HT / mois</span>
                </p>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/80 sm:text-base">
                  Un abonnement simple pour accéder à l'écosystème CozyHome et développer votre activité au sein du
                  réseau.
                </p>
                <p data-testid="partenaire-candidature-no-payment" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-bold">
                  <Check className="h-3.5 w-3.5" /> Aucun paiement n'est demandé lors de votre candidature.
                </p>
              </div>
            </article>
          </Reveal>

          {/* ---------------- Comment ça fonctionne ---------------- */}
          <Reveal>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: PETROLE }}>Le parcours</p>
              <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">Comment ça fonctionne ?</h2>
              <ol data-testid="partenaire-candidature-etapes" className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {STEPS.map(([t, d], i) => (
                  <li key={t} className="rounded-3xl bg-white p-6">
                    <span className="font-display text-2xl font-medium" style={{ color: PETROLE }}>{String(i + 1).padStart(2, "0")}</span>
                    <p className="mt-3 text-sm font-bold text-navy">{t}</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-stone-500">{d}</p>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>

          {/* ---------------- Formulaire ---------------- */}
          <Reveal>
            <article id="candidature" data-testid="partenaire-candidature-form-section" className="scroll-mt-28 overflow-hidden rounded-3xl bg-white">
              <div className="h-1.5 w-full" style={{ backgroundColor: PETROLE }} aria-hidden="true" />
              <div className="p-7 sm:p-12">
                {submitted ? (
                  <div data-testid="partenaire-candidature-success" className="py-8 text-center">
                    <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: `${PETROLE}14`, color: PETROLE }}>
                      <Check className="h-7 w-7" />
                    </span>
                    <h2 className="mt-6 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">Merci pour votre candidature</h2>
                    <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-stone-500 sm:text-base">
                      Votre demande a bien été transmise à CozyHome. Nous allons étudier votre profil et votre
                      secteur géographique. Si votre candidature est pré-validée, nous vous contacterons pour
                      poursuivre votre dossier et vous demander les documents nécessaires.
                    </p>
                    <p className="mx-auto mt-3 max-w-xl text-sm font-semibold text-stone-500">
                      Aucun document ni paiement n'est nécessaire à ce stade.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: PETROLE }}>Candidature</p>
                    <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">Présentez votre profil</h2>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-stone-500 sm:text-base">
                      Ce formulaire ne demande aucun document. Les justificatifs ne seront demandés qu'après une
                      première validation de votre profil.
                    </p>

                    <form onSubmit={handleSubmit} className="mt-8 space-y-8">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Vos coordonnées</p>
                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <input data-testid="candidature-firstname" required value={form.firstname} onChange={(e) => set("firstname", e.target.value)}
                            placeholder="Prénom *" autoComplete="given-name" className={inputClass} />
                          <input data-testid="candidature-lastname" required value={form.lastname} onChange={(e) => set("lastname", e.target.value)}
                            placeholder="Nom *" autoComplete="family-name" className={inputClass} />
                          <input data-testid="candidature-phone" required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                            placeholder="Téléphone *" autoComplete="tel" className={inputClass} />
                          <input data-testid="candidature-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                            placeholder="E-mail *" autoComplete="email" className={inputClass} />
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Situation professionnelle *</p>
                        <div className="mt-4 space-y-2.5">
                          {SITUATIONS.map((s) => (
                            <label
                              key={s.value}
                              data-testid={`candidature-situation-${s.value}`}
                              className={`flex cursor-pointer items-start gap-3 rounded-2xl border-2 px-5 py-4 transition-colors duration-200 ${
                                form.situation === s.value ? "border-[#284E55] bg-[#284E55]/5" : "border-[#EDEAE3] bg-white hover:border-[#284E55]/40"
                              }`}
                            >
                              <input type="radio" name="situation" className="mt-0.5 h-5 w-5 shrink-0 accent-[#284E55]"
                                checked={form.situation === s.value} onChange={() => set("situation", s.value)} />
                              <span className="text-sm leading-relaxed text-navy/85">{s.label}</span>
                            </label>
                          ))}
                        </div>
                        {form.situation === "installe" && (
                          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <input data-testid="candidature-company-name" value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
                              placeholder="Nom de l'entreprise (facultatif)" className={inputClass} />
                            <input data-testid="candidature-siret" value={form.siret} onChange={(e) => set("siret", e.target.value)}
                              placeholder="SIRET (facultatif)" className={inputClass} />
                          </div>
                        )}
                      </div>

                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Localisation</p>
                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <input data-testid="candidature-city" required value={form.city} onChange={(e) => set("city", e.target.value)}
                            placeholder="Ville *" className={inputClass} />
                          <input data-testid="candidature-postal-code" required value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)}
                            placeholder="Code postal *" inputMode="numeric" className={inputClass} />
                          <input data-testid="candidature-department" required value={form.department} onChange={(e) => set("department", e.target.value)}
                            placeholder="Département *" className={inputClass} />
                          <input data-testid="candidature-sector" required value={form.sector} onChange={(e) => set("sector", e.target.value)}
                            placeholder="Secteur géographique souhaité *" className={inputClass} />
                        </div>
                        <TerritoryCheck
                          postalCode={form.postal_code}
                          city={form.city}
                          chosenId={form.intended_territory_id}
                          onChoose={(t) => setForm((f) => ({ ...f, intended_territory_id: t.id, sector: t.name, department: t.department }))}
                        />
                      </div>

                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Êtes-vous déjà maître d'œuvre ? *</p>
                        <div className="mt-3 flex gap-3">
                          <Pill testid="candidature-moe-oui" selected={form.already_moe === "oui"} onClick={() => set("already_moe", "oui")} label="Oui" />
                          <Pill testid="candidature-moe-non" selected={form.already_moe === "non"} onClick={() => set("already_moe", "non")} label="Non" />
                        </div>
                      </div>

                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Expérience dans le bâtiment / la rénovation *</p>
                        <div className="mt-3 flex flex-wrap gap-3">
                          {EXPERIENCES.map((ex) => (
                            <Pill key={ex.value} testid={`candidature-experience-${ex.value}`} selected={form.experience === ex.value}
                              onClick={() => set("experience", ex.value)} label={ex.label} />
                          ))}
                        </div>
                        {form.experience === "aucune" && (
                          <p data-testid="candidature-experience-note" className="mt-3 flex items-start gap-2 rounded-2xl p-4 text-xs leading-relaxed" style={{ backgroundColor: `${PETROLE}0D`, color: PETROLE }}>
                            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            Aucune expérience ? Ce n'est pas bloquant : CozyHome peut vous accompagner dans le démarrage de votre activité.
                          </p>
                        )}
                      </div>

                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Parlez-nous de votre projet</p>
                        <textarea
                          data-testid="candidature-message"
                          value={form.message}
                          onChange={(e) => set("message", e.target.value)}
                          placeholder="Pourquoi souhaitez-vous rejoindre CozyHome ? Présentez-nous en quelques mots votre parcours et votre projet."
                          rows={5}
                          className={`${inputClass} resize-none`}
                        />
                      </div>

                      <label className="flex cursor-pointer items-start gap-3 rounded-2xl border-2 border-[#EDEAE3] bg-white px-5 py-4">
                        <input data-testid="candidature-consent" type="checkbox" checked={form.consent}
                          onChange={(e) => set("consent", e.target.checked)}
                          className="mt-0.5 h-5 w-5 shrink-0 accent-[#284E55]" />
                        <span className="text-sm leading-relaxed text-navy/80">
                          J'accepte que les informations transmises soient utilisées par CozyHome pour traiter ma
                          candidature, conformément à la politique de confidentialité. <span style={{ color: PETROLE }}>*</span>
                        </span>
                      </label>

                      <button
                        data-testid="candidature-submit"
                        type="submit"
                        disabled={submitting}
                        className="flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-sm font-bold text-white transition-opacity duration-300 hover:opacity-90 disabled:opacity-60 sm:w-auto"
                        style={{ backgroundColor: PETROLE }}
                      >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                        Envoyer ma candidature
                      </button>
                    </form>
                  </>
                )}
              </div>
            </article>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
